import { InfoCard } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { RiDatabase2Line } from '@remixicon/react';
import {
  InfraCodeBlock,
  InfraField,
  InfraSection,
  InfraStack,
  InfraTag,
  InfraTagValue,
  InfraTagRow,
  Text,
} from '../ui/InfraPrimitives';

export const ElastiCacheInfoCard = () => {
  const { entity } = useEntity();
  const annotations = entity.metadata.annotations || {};

  const templateType = annotations['infrastructure.backstage.io/type'] || '';
  const clusterId = annotations['elasticache.infrastructure/cluster-id'];
  const clusterAddress = annotations['elasticache.infrastructure/cluster-address'];
  const clusterPort = annotations['elasticache.infrastructure/cluster-port'];
  const connectionString = annotations['elasticache.infrastructure/connection-string'];
  const engine = annotations['elasticache.infrastructure/engine'];
  const engineVersion = annotations['elasticache.infrastructure/engine-version'];
  const nodeType = annotations['elasticache.infrastructure/node-type'];
  const numCacheNodes = annotations['elasticache.infrastructure/num-cache-nodes'];
  const region = annotations['aws.infrastructure/region'];

  const hasElastiCacheInfo =
    templateType === 'aws-elasticache' && (clusterId || clusterAddress);

  if (!hasElastiCacheInfo) {
    return null;
  }

  return (
    <InfoCard
      title="ElastiCache Cluster Information"
      icon={<RiDatabase2Line size={20} />}
    >
      <InfraStack>
        <InfraSection title="☁️ Cluster Configuration">
          {clusterId && <InfraField label="Cluster ID:" value={clusterId} />}
          {engine && (
            <InfraField
              label="Engine:"
              value={
                <InfraTagRow>
                  <InfraTag>
                    {engine} {engineVersion || ''}
                  </InfraTag>
                </InfraTagRow>
              }
              mono={false}
            />
          )}
          {nodeType && (
            <InfraField
              label="Node Type:"
              value={<InfraTagValue>{nodeType}</InfraTagValue>}
              mono={false}
            />
          )}
          {numCacheNodes && (
            <InfraField
              label="Number of Nodes:"
              value={<InfraTagValue>{numCacheNodes}</InfraTagValue>}
              mono={false}
            />
          )}
          {region && (
            <InfraField
              label="AWS Region:"
              value={<InfraTagValue>{region}</InfraTagValue>}
              mono={false}
            />
          )}
        </InfraSection>

        <InfraSection title="🔌 Connection Information">
          {clusterAddress && (
            <InfraField label="Endpoint:" value={clusterAddress} />
          )}
          {clusterPort && <InfraField label="Port:" value={clusterPort} />}
          {connectionString && (
            <InfraField label="Connection String:" value={connectionString} />
          )}
        </InfraSection>

        {clusterAddress && clusterPort && (
          <InfraSection title="📝 Connection">
            <Text variant="body-medium">
              ElastiCache has no public endpoint. The hostname resolves to a
              private VPC address, so redis-cli from a laptop fails with
              Network is unreachable. Connect from a VM in the same VPC (for
              example aws-empty-vm), or tunnel through that VM:
            </Text>
            <InfraCodeBlock variant="light">
              {`ssh -L ${clusterPort}:${clusterAddress}:${clusterPort} ubuntu@<vm-public-ip>
redis-cli -h 127.0.0.1 -p ${clusterPort}`}
            </InfraCodeBlock>
          </InfraSection>
        )}
      </InfraStack>
    </InfoCard>
  );
};
